import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Mail, Phone, MapPin, Linkedin, Twitter, ShieldCheck } from 'lucide-react';
import { BUSINESS_NAME, NAV_LINKS } from '@/src/constants';

export default function Footer() {
  return (
    <footer className="bg-background border-t border-white/5 pt-16 pb-10 relative overflow-hidden">
      <div className="container mx-auto px-4 md:px-8 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10 mb-16">
          {/* Brand Column */}
          <div className="lg:col-span-4 space-y-5">
            <Link to="/" className="flex items-center gap-2.5 group">
              <Shield className="w-6 h-6 text-accent transition-transform duration-500 group-hover:scale-110" />
              <span className="font-display font-bold text-lg tracking-tighter text-foreground uppercase">
                {BUSINESS_NAME}
              </span>
            </Link>
            <p className="text-muted-foreground text-sm leading-relaxed font-normal max-w-md">
              Practical cybersecurity solutions for retail, e-commerce, and small to mid-size businesses across Washington DC, Virginia, and Maryland.
            </p>
            <div className="flex items-center gap-3">
              <a 
                href="https://www.linkedin.com/in/fabiyan-anik" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="w-8 h-8 rounded-xl bg-white/[0.01] border border-white/10 flex items-center justify-center text-muted-foreground hover:text-accent hover:border-accent hover:bg-accent/5 transition-all"
                aria-label="Connect with Fabiyan Anik on LinkedIn"
              >
                <Linkedin className="w-4 h-4" />
              </a>
              <a 
                href="https://twitter.com/agrandintl" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-8 h-8 rounded-xl bg-white/[0.01] border border-white/10 flex items-center justify-center text-muted-foreground hover:text-accent hover:border-accent hover:bg-accent/5 transition-all"
                aria-label="Follow us on X"
              >
                <Twitter className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div className="lg:col-span-2">
            <h4 className="text-[8px] uppercase tracking-[0.3em] font-bold text-foreground mb-5 text-accent/60">Navigation</h4>
            <ul className="space-y-2.5">
              {NAV_LINKS.map((link) => (
                <li key={link.name}>
                  <Link to={link.href} className="text-muted-foreground hover:text-accent text-[9px] transition-all uppercase tracking-widest font-bold block hover:translate-x-1">
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Governance */}
          <div className="lg:col-span-2">
            <h4 className="text-[8px] uppercase tracking-[0.3em] font-bold text-foreground mb-5 text-accent/60">Governance</h4>
            <ul className="space-y-2.5">
              <li><Link to="/security-compliance" className="text-muted-foreground hover:text-accent text-[10px] transition-all block font-bold hover:translate-x-1">Trust Hub</Link></li>
              <li><Link to="/security-policy" className="text-muted-foreground hover:text-accent text-[10px] transition-all block hover:translate-x-1 uppercase tracking-tighter">Security</Link></li>
              <li><Link to="/data-protection-policy" className="text-muted-foreground hover:text-accent text-[10px] transition-all block hover:translate-x-1 uppercase tracking-tighter">Data Protection</Link></li>
              <li><Link to="/incident-response-policy" className="text-muted-foreground hover:text-accent text-[10px] transition-all block hover:translate-x-1 uppercase tracking-tighter">Incident</Link></li>
            </ul>
          </div>

          {/* Solutions */}
          <div className="lg:col-span-2">
            <h4 className="text-[8px] uppercase tracking-[0.3em] font-bold text-foreground mb-5 text-accent/60">Solutions</h4>
            <ul className="space-y-2.5">
              <li><Link to="/services" className="text-muted-foreground hover:text-accent text-[10px] transition-all block hover:translate-x-1">Cyber Audit</Link></li>
              <li><Link to="/complytics" className="text-muted-foreground hover:text-accent text-[10px] transition-all block hover:translate-x-1">Compliance AI</Link></li>
              <li><Link to="/industries" className="text-muted-foreground hover:text-accent text-[10px] transition-all block hover:translate-x-1">Retail Sec</Link></li>
              <li><Link to="/services" className="text-muted-foreground hover:text-accent text-[10px] transition-all block hover:translate-x-1">Cloud Gov</Link></li>
            </ul>
          </div>

          {/* Contact Info */}
          <div className="lg:col-span-2">
            <h4 className="text-[8px] uppercase tracking-[0.3em] font-bold text-foreground mb-5 text-accent/60">Contact</h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-2 group">
                <a href="mailto:contact@agrandintl.com" className="text-muted-foreground hover:text-accent text-[9px] font-bold transition-colors uppercase tracking-widest break-all">
                  Email Us
                </a>
              </li>
              <li className="flex items-start gap-2 group">
                <a href="tel:+12026306862" className="text-muted-foreground hover:text-accent text-[9px] font-bold transition-colors uppercase tracking-widest">
                  +1 202 630 6862
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/5 pt-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex flex-col md:flex-row items-center gap-6 text-center md:text-left">
            <div className="flex items-center gap-3">
              <ShieldCheck className="w-3.5 h-3.5 text-accent" />
              <p className="text-muted-foreground text-[8px] uppercase tracking-[0.2em] font-bold">
                Certified Cybersecurity Consulting | DMV Area
              </p>
            </div>
          </div>
          <div className="flex flex-col items-center md:items-end gap-1.5">
            <div className="flex flex-wrap items-center justify-center md:justify-end gap-x-3 gap-y-1 mb-0.5 opacity-50 hover:opacity-100 transition-opacity">
              <Link to="/access-control-policy" className="text-[6px] text-muted-foreground hover:text-accent uppercase tracking-widest font-bold transition-colors">Access</Link>
              <Link to="/vulnerability-management-policy" className="text-[6px] text-muted-foreground hover:text-accent uppercase tracking-widest font-bold transition-colors">Vuln</Link>
              <Link to="/logging-monitoring-policy" className="text-[6px] text-muted-foreground hover:text-accent uppercase tracking-widest font-bold transition-colors">Logs</Link>
              <Link to="/business-continuity-policy" className="text-[6px] text-muted-foreground hover:text-accent uppercase tracking-widest font-bold transition-colors">BCP</Link>
              <Link to="/privacy-policy" className="text-[6px] text-muted-foreground hover:text-accent uppercase tracking-widest font-bold transition-colors">Privacy</Link>
              <Link to="/terms-of-service" className="text-[6px] text-muted-foreground hover:text-accent uppercase tracking-widest font-bold transition-colors">Terms</Link>
            </div>
            <p className="text-muted-foreground text-[8px] uppercase tracking-[0.2em] font-medium">
              © {new Date().getFullYear()} {BUSINESS_NAME}. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
